import { transform } from "sucrase";
import type { TestOutcome } from "../bank/types.ts";

/** A syntax error located in the submitted code. Offsets are into the editor document. */
export type SyntaxFailure = {
  message: string;
  /** 0-based character offset into the submitted code. */
  pos: number;
  /** 1-based, for display. */
  line: number;
  /** 1-based, for display. */
  column: number;
};

export type ExecuteResult =
  | { ok: true; outcomes: TestOutcome[]; logs: string[] }
  | { ok: false; phase: "compile"; message: string; syntax?: SyntaxFailure; logs: string[] }
  | { ok: false; phase: "execute"; message: string; logs: string[] };

const MAX_LOGS = 200;
const MAX_LOG_LENGTH = 2000;

function render(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

// Iterates the harness's `__tests` and turns each throw into a failure. Written
// as ES5-flavoured source because it is concatenated into the compiled output
// rather than compiled from this file.
const EPILOGUE = `
;return (async function () {
  if (typeof __tests === "undefined") {
    throw new Error("The drill harness did not define __tests.");
  }
  var __outcomes = [];
  for (var __i = 0; __i < __tests.length; __i++) {
    var __t = __tests[__i];
    try {
      // Each case starts at t=0 with no timers left over, the same way each run
      // starts in a fresh worker. Without this, cases silently depend on the
      // order they are declared in.
      if (typeof __clock !== "undefined") __clock.reset();
      // Awaited, so a drill about promises can assert on settled results. A
      // synchronous run() is unaffected: awaiting a non-promise is a no-op.
      await __t.run();
      __outcomes.push({ name: __t.name, hidden: !!__t.hidden, about: __t.about, passed: true });
    } catch (__e) {
      __outcomes.push({
        name: __t.name,
        hidden: !!__t.hidden,
        about: __t.about,
        passed: false,
        message: __e && __e.message ? String(__e.message) : String(__e)
      });
    }
  }
  return __outcomes;
})();`;

/**
 * Parse the submitted code on its own and report where it broke.
 *
 * Standalone, not concatenated with the harness, and that matters: an unclosed
 * brace makes the parser swallow whatever follows and report the failure at the
 * end of it. Compiled together, a missing `}` on line 3 surfaces as an error
 * deep inside the harness, which is both useless and a lie about whose code is
 * wrong. Parsed alone, the position always lands in the editor.
 *
 * Sucrase's message ends in "(line:column)". That is stripped, because the
 * position is carried structurally and shown by the squiggle and the gutter;
 * repeating it in the text is noise.
 */
export function checkSyntax(code: string): SyntaxFailure | null {
  try {
    transform(code, { transforms: ["typescript"], disableESTransforms: true });
    return null;
  } catch (error) {
    const e = error as { message?: string; pos?: number; loc?: { line: number; column: number } };
    const raw = e.message ?? String(error);
    const message = raw.replace(/\s*\(\d+:\d+\)\s*$/, "");
    // Clamp: a position past the end would place a diagnostic CodeMirror rejects.
    const pos = Math.max(0, Math.min(typeof e.pos === "number" ? e.pos : 0, code.length));
    return {
      message,
      pos,
      line: e.loc?.line ?? 1,
      column: e.loc?.column ?? 0,
    };
  }
}

/**
 * The seed a harness generates its inputs from, declared above everything else
 * so the prelude, the submitted code and the harness all see the same number.
 *
 * Coerced hard: this is interpolated into source, so anything that is not a
 * finite integer would be a way to inject code through a value that is only
 * supposed to be a number.
 */
function seedDeclaration(seed: number): string {
  const safe = Number.isFinite(seed) ? Math.trunc(seed) : 0;
  return `var __seed = ${safe};`;
}

/**
 * Strip the types off the submitted code, append the drill's harness, and run
 * the lot as one function body.
 *
 * Types are stripped rather than checked, deliberately. A drill is judged on
 * behaviour, the same as a real screen, so an annotation that is wrong but
 * harmless at runtime should not cost you the attempt.
 *
 * This does not protect against an infinite loop. Nothing in-process can: the
 * caller runs it in a worker and terminates that worker on a timeout.
 *
 * `seed` feeds the harness kit's generators. Same seed, same inputs, so a run
 * is reproducible and iterating on one attempt does not move the target.
 */
export async function executeAttempt(
  code: string,
  harness: string,
  prelude = "",
  seed = 0,
): Promise<ExecuteResult> {
  const logs: string[] = [];
  const capture = (...args: unknown[]) => {
    if (logs.length >= MAX_LOGS) return;
    logs.push(args.map(render).join(" ").slice(0, MAX_LOG_LENGTH));
  };
  const sandboxConsole = { log: capture, info: capture, warn: capture, error: capture, debug: capture };

  // Blame the submitted code first, and only then the harness.
  const syntax = checkSyntax(code);
  if (syntax) {
    return { ok: false, phase: "compile", message: syntax.message, syntax, logs };
  }

  let compiled: string;
  try {
    compiled = transform([seedDeclaration(seed), prelude, code, harness, EPILOGUE].join("\n"), {
      transforms: ["typescript"],
      disableESTransforms: true,
    }).code;
  } catch (error) {
    // The submitted code parsed on its own but the combined unit did not, so
    // this is the drill's harness being broken, not the candidate's code. Say so
    // rather than pointing at a line the user cannot see.
    return {
      ok: false,
      phase: "compile",
      message: `This drill's tests failed to compile, which is a bug in the drill rather than in your code. ${
        error instanceof Error ? error.message : String(error)
      }`,
      logs,
    };
  }

  try {
    const body = new Function("console", compiled) as (c: unknown) => Promise<TestOutcome[]>;
    return { ok: true, outcomes: await body(sandboxConsole), logs };
  } catch (error) {
    // A throw out here means nothing ran at all: a broken top level rather than
    // a failing test. Worth reporting differently, since the fix is different.
    return {
      ok: false,
      phase: "execute",
      message: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
      logs,
    };
  }
}
